

import { useMemo, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Button } from "@/shared/components/Button";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { getSellerAbsoluteUrl } from "@/lib/routing/portal-urls";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateStoreImpersonationSessionMutation } from "@/shared/services/useCreateStoreImpersonationSessionMutation";
import { useSessionQuery } from "@/shared/services/useSessionQuery";

const ALLOWED_IMPERSONATION_ROLES = [
  ADMIN_ROLES.ADMIN,
  ADMIN_ROLES.SUPER_ADMIN,
  ADMIN_ROLES.CUSTOMER_SERVICE,
] as const;

function hasImpersonationAccess(roles?: readonly string[] | null) {
  if (!roles?.length) {
    return false;
  }
  return roles.some((role) =>
    ALLOWED_IMPERSONATION_ROLES.includes(role as (typeof ALLOWED_IMPERSONATION_ROLES)[number]),
  );
}

type Props = {
  storeId: string;
  locale: string;
  adminRoles?: string[];
  className?: string;
  variant?: "outline" | "ghost";
  size?: "sm" | "md";
  hideError?: boolean;
  onActionStart?: () => void;
};

export function ImpersonateButton({
  storeId,
  locale,
  adminRoles,
  className,
  variant = "outline",
  size = "sm",
  hideError = false,
  onActionStart,
}: Props) {
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const { mutateAsync: createImpersonationSession } =
    useCreateStoreImpersonationSessionMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolvedRoles = adminRoles ?? session?.admin?.roles ?? null;

  const canImpersonate = useMemo(
    () => hasImpersonationAccess(resolvedRoles),
    [resolvedRoles],
  );

  if ((adminRoles === undefined && isSessionLoading) || !canImpersonate) {
    return null;
  }

  const handleClick = async () => {
    onActionStart?.();
    setError(null);
    const openedTab = window.open("about:blank", "_blank");
    if (openedTab) {
      openedTab.document.write("<title>Opening seller portal...</title>");
    }
    if (!openedTab) {
      setError(t(keys.errors.generic));
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = await createImpersonationSession({ storeId });
      const sellerUrl = getSellerAbsoluteUrl(locale, payload.storeSlug);
      openedTab.location.assign(sellerUrl);
    } catch (caughtError) {
      if (openedTab) {
        openedTab.close();
      }
      if (caughtError instanceof ApiJsonClientError) {
        setError(caughtError.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant={variant}
        size={size}
        className={["max-lg:min-h-10", className].filter(Boolean).join(" ")}
        disabled={isSubmitting}
        onClick={handleClick}
      >
        {isSubmitting
          ? t(keys.admin.impersonateSellerPending)
          : t(keys.admin.impersonateSellerAction)}
      </Button>
      {!hideError && error ? (
        <p className="text-xs text-[color:var(--error-600)]" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
